import React, { useMemo, useState } from "react";
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    TextField,
    Typography,
    Box,
    TablePagination,
    TableSortLabel,
} from "@mui/material";

const WidgetTable = ({
                         columns = [],
                         rows = [],
                         getRowId,
                         onRowClick,
                         emptyMessage = "Sin información",
                         searchPlaceholder = "Buscar...",
                         rowsPerPageOptions = [10, 25, 50, 100],
                         defaultRowsPerPage = 10,
                         defaultOrderBy = null,
                         defaultOrder = "asc",
                         enableSearch
                     }) => {
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
    const [orderBy, setOrderBy] = useState(defaultOrderBy);
    const [order, setOrder] = useState(defaultOrder);

    const filteredRows = useMemo(() => {
        if (!rows?.length) return [];
        if (!search) return rows;

        const searchValue = search.toLowerCase();

        return rows.filter((row) =>
            columns.some((col) => {
                const value = row[col.field];
                if (value === null || value === undefined) return false;

                return value
                    .toString()
                    .toLowerCase()
                    .includes(searchValue);
            })
        );
    }, [rows, search, columns]);

    const sortedRows = useMemo(() => {
        if (!orderBy) return filteredRows;

        return [...filteredRows].sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];

            if (aValue == null) return 1;
            if (bValue == null) return -1;

            // Numeric
            if (!isNaN(aValue) && !isNaN(bValue)) {
                return order === "asc"
                    ? aValue - bValue
                    : bValue - aValue;
            }

            // String
            return order === "asc"
                ? aValue.toString().localeCompare(bValue.toString())
                : bValue.toString().localeCompare(aValue.toString());
        });
    }, [filteredRows, orderBy, order]);

    const paginatedRows = useMemo(() => {
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        return sortedRows.slice(start, end);
    }, [sortedRows, page, rowsPerPage]);

    const handleChangePage = (_, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSort = (field) => {
        if (orderBy === field) {
            setOrder(order === "asc" ? "desc" : "asc");
        } else {
            setOrderBy(field);
            setOrder("asc");
        }
    };

    return (
        <TableContainer component={Paper}>
            {/* SEARCH */}
            <Box p={2} display="flex" gap={2}>
                {enableSearch && (
                <TextField
                    size="small"
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0);
                    }}
                />
                )}
            </Box>

            <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                    <TableHead sx={{ backgroundColor: "#0A2342" }}>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell key={col.field}>
                                    {col.sortable === false ? (
                                        <Typography fontWeight={600} sx={{ color: "white" }}>
                                            {col.header}
                                        </Typography>
                                    ) : (
                                        <TableSortLabel
                                            active={orderBy === col.field}
                                            direction={orderBy === col.field ? order : "asc"}
                                            onClick={() => handleSort(col.field)}
                                            sx={{
                                                color: "white",
                                                "&.Mui-active": { color: "white" },
                                                "& .MuiTableSortLabel-icon": {
                                                    color: "white !important",
                                                },
                                            }}
                                        >
                                            <Typography fontWeight={600}>
                                                {col.header}
                                            </Typography>
                                        </TableSortLabel>
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginatedRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {emptyMessage}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedRows.map((row, index) => {
                                const rowId = getRowId
                                    ? getRowId(row)
                                    : row.id ?? index;

                                return (
                                    <TableRow
                                        key={rowId}
                                        hover={!!onRowClick}
                                        sx={{
                                            cursor: onRowClick ? "pointer" : "default",
                                        }}
                                        onClick={() => onRowClick?.(row)}
                                    >
                                        {columns.map((col) => (
                                            <TableCell
                                                key={col.field}
                                                sx={{
                                                    maxWidth: 250,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {col.render
                                                    ? col.render(row)
                                                    : row[col.field] ?? "-"}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <TablePagination
                    component="div"
                    count={filteredRows.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions}
                    labelRowsPerPage="Registros"
                />
            </Box>
        </TableContainer>
    );
};

export default WidgetTable;
